import {
  Component,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ElementRef,
  Renderer2,
  ViewChild,
  Output,
  EventEmitter
} from '@angular/core';
import {WizTranslatableComponent} from '../../../core/wiz-translatable-component';
import { CODE_SPACE, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';
import { WizTranslableProperty } from '../../../../services/translation/wiz-translatable-property.decorator';

/**
 * Classe du composant wiz-toggle-switch
 */
@Component({
  selector: 'wiz-toggle',
  templateUrl: './wiz-toggle.html',
  styleUrls: ['./wiz-toggle.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizToggle extends WizTranslatableComponent {

  /** Dispatch l'event de change sur un output */
  @Output() wizChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * the element corresponding to the slider
   */
  @ViewChild('switchSlider', {static: true, read: ElementRef}) switchSlider: ElementRef;


  /**
   * the status of the switch
   *
   * this value change every time the user click or swipe on the switch
   */
  @Input()
  public set wizChecked(v: boolean) {
    this.setWizChecked(v);
  }

  public get wizChecked(): boolean {
    return this.getWizChecked();
  }
  private _wizChecked: boolean = false;

  protected setWizChecked(v: boolean) {
    this._wizChecked = v;
    this.updateSliderPosition();
    this.cdref.markForCheck();
  }
  protected getWizChecked(): boolean {
    return this._wizChecked;
  }
  /**
   * the switch type.
   *
   * if true, the toggle switch background images or text won't appeare.
   */
  @Input()
  public set checkboxType(v: boolean) {
    this.setCheckboxType(v);
  }

  public get checkboxType(): boolean {
    return this.getCheckboxType();
  }
  private _checkboxType: boolean = false;

  protected setCheckboxType(v: boolean) {
    this._checkboxType = v;
    this.cdref.markForCheck();
  }
  protected getCheckboxType(): boolean {
    return this._checkboxType;
  }
  /**
   * src de l'image servant d'icon droit
   */
  @Input()
  public set rightIcon(v: string) {
    this.setRightIcon(v);
  }

  public get rightIcon(): string {
    return this.getRightIcon();
  }
  private _rightIcon: string;

  protected setRightIcon(v: string) {
    this._rightIcon = v;
    this.cdref.markForCheck();
  }
  protected getRightIcon(): string {
    return this._rightIcon;
  }

  /**
   * src de l'image servant d'icone gauche
   */
  @Input()
  public set leftIcon(v: string) {
    this.setLeftIcon(v);
  }

  public get leftIcon(): string {
    return this.getLeftIcon();
  }
  private _leftIcon: string;

  protected setLeftIcon(v: string) {
    this._leftIcon = v;
    this.cdref.markForCheck();
  }
  protected getLeftIcon(): string {
    return this._leftIcon;
  }
  /**
   * texte de la partie droite du toggle
   */
  public _rightText: string = 'NO';
  @WizTranslableProperty('rightTranslatedText')
  @Input() public set rightText(text: string) {
    this.setRightText(text);
  }
  /** _rightText traduit */
  public rightTranslatedText: string;

  protected setRightText(text: string) {
    this._rightText = text;
    this.wizRegisterTranslation();
    this.cdref.markForCheck();
  }
  /**
   * texte de la partie gauche du toggle
   */
  public _leftText: string = 'YES';
  @WizTranslableProperty('leftTranslatedText')
  @Input() public set leftText(text: string) {
    this.setLeftText(text);
  }
  /** _leftText traduit */
  public leftTranslatedText: string;

  protected setLeftText(text: string) {
    this._leftText = text;
    this.wizRegisterTranslation();
    this.cdref.markForCheck();
  }

  /**
   * indicates if the last user action on the switch was a drag
   *
   * if true the other event like click won't be executed.
   * the reason of this propertie is that sometimes the pan events trigger a click
   */
  public sliderDragged: boolean = false;

  /**
   * the distance from the start of the mouse drag to the left side of the cursor
   *
   * this property is set in the panSart methode and used in panMove
   */
  public firstDeltaX: number = 0;

  /**
   * defines whether the slider is pressed or not
   *
   * this property is used to change the brightness of the slider when it's pressed.
   */
  public sliderIsPressed: boolean = false;

  /** Constructor with translation engine
   * @param cdref: ChangeDetectorRef
   * @param elementRef: the dom of the component
   * @param renderer: object that will allow the interaction between the component and the DOM
   */
  constructor(cdref: ChangeDetectorRef, elementRef: ElementRef, renderer: Renderer2) {
    super(cdref, elementRef, renderer);
  }

  /**
   * changes the value of wizChecked and updates the position of the slider
   * @param e, an object emited with the click event
   */
  public onClick(e: any): void {
    this.sliderIsPressed = true;
    // if the switch slider was set by the panEnd methode and the sliderDragged is false
    if (!this.sliderDragged) {
      this.wizChecked = !this.wizChecked;
      this.updateSliderPosition();
      this.onChange(e);

    } else {
      this.sliderDragged = false;
    }
    this.sliderIsPressed = false;
  }

  /**
   * mise à jour de la position du slider
   */
  public updateSliderPosition(): void {
    if (!this.wizChecked) {
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', '2px');
    } else {
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', 'calc(50% - 2px)');
    }
  }

  /**
   * gestion du keyDown pour déplacer le slider et mettre à jour le status du toggle si c'est la touche espace
   * @param event, event déclenché par les touches du clavier
   */
  public onKeyDown(event: KeyboardEvent): void {
    if (event.code === CODE_SPACE) {
      this.sliderIsPressed = true;
      // if the switch slider was set by the panEnd methode and the sliderDragged is false
      if (!this.sliderDragged) {
        this.wizChecked = !this.wizChecked;
        this.updateSliderPosition();
        this.onChange(event);

      } else {
        this.sliderDragged = false;
      }
      this.sliderIsPressed = false;
      // stop de la propagation de l'event, pour ne pas faire scroller la page
      event.preventDefault();
    }
  }

  /**
   * sets the sliderIsPressed to true
   * @param e, an object emited with the press event
   *
   * this methode is only for design puporse
   */
  public onPress(e: any): void {
    this.sliderIsPressed = true;
  }

  /**
   * sets the final position of the slider
   * @param action, object emited with the panEnd event
   *
   * this methode sets the switchSlider property and sets the sliderDragged property to true, so that
   * even if it triggers a click, there will be no side-effect.
   */
  public panEnd(action: any): void {
    //  the size of the component and its position relative to the viewport.
    const myElem = this.elementRef.nativeElement.getBoundingClientRect();

    //  the size of the slider and its position relative to the viewport.
    const sliderRect = action.target.getBoundingClientRect();

    // the quarter of the toggle witdh
    const switchWidthQuarter = myElem.width / 4;
    // if the slider is near to the right side when the user release it
    if (sliderRect.right >= (myElem.right - switchWidthQuarter)) {
      // we finish the slide for the user
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', 'calc(50% - 2px)');
      this.wizChecked = true;
      this.onChange(action.srcEvent);

      // if the slider is near to the left side when the user release it
    } else if (sliderRect.left <= (myElem.left + switchWidthQuarter)) {
      // we finish the slide for the user
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', '2px');
      this.wizChecked = false;
      this.onChange(action.srcEvent);

      // if the slider is in the middle and was on the right side (wizChecked === true) on the begining
    } else if (this.wizChecked) {
      // we reset the slider position
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', 'calc(50% - 2px)');

      // if the slider is in the middle and was on the left side (wizChecked === false)  on the begining
    } else {
      // we reset the slider position
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', '2px');
    }
    this.sliderDragged = true;
    this.sliderIsPressed = false;
  }

  /**
   * gets the coordinates of the cursor when the drag begins
   * @param action, element containing the fired event info such as the target element
   */
  public onPanStart(action: any): void {
    const sliderRect = action.target.getBoundingClientRect();
    this.firstDeltaX = action.center.x - sliderRect.left;
    this.sliderIsPressed = true;
  }

  /**
   * methode called when the slider is dragged from one side to another
   * @param action, element containing the fired event info such as the mouse position
   *               action.type is direction of the movement  (ex: right, for from left to right)
   *               action.deltaX is the number of pixel that the mouse has moved through
   */
  public onPanMove(action: any): void {
    const myElem = this.elementRef.nativeElement.getBoundingClientRect();
    const sliderRect = action.target.getBoundingClientRect();
    const maxLeft = 50;

    const leftBorder = myElem.right >= sliderRect.right;
    const rightBorder = myElem.left <= sliderRect.left;

    const myDeltaX = action.center.x - this.firstDeltaX - myElem.left;

    if (leftBorder && rightBorder && myDeltaX >= 0 && myDeltaX <= maxLeft) {
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', myDeltaX + '%');
    }
    if (action.center.x >= myElem.right) {
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', 'calc(50% - 2px)');
    } else if (action.center.x <= myElem.left) {
      this.renderer.setStyle(this.switchSlider.nativeElement, 'left', '2px');
    }

  }


  /**
   * this methode return the text to display according to the state of the switch
   */
  public getSelectedText(): string {
    return this.wizChecked ? this.rightTranslatedText : this.leftTranslatedText;
  }

  /** Selection change handler */
  public onChange(e: any): void {
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.getSelectedText()));
    this.wizChange.emit(this.wizChecked);
  }

}
